import { ISortTable, RecurrenceRule } from "../../../utils/types";
import {
    ProviderStatus,
    SessionServiceType,
} from "../../../utils/types/session";
import {
    FlaggedNoteForAuditStatus,
    NoteStatus,
    NoteTypes,
} from "../../notes/types/notes.types";

export enum ClientCardTypes {
    DISCOVER_FINANCIAL = "Discover",
    AMERICAN_EXPRESS = "AmericanExpress",
    MASTERCARD = "MasterCard",
    CAPITAL_ONE = "Capital One",
    CHASE = "Chase",
    VISA = "Visa",
    DINERSCLUB = "DinersClub",
    JCB = "JCB",
}

export enum ProviderCredential {
    PHD = "PhD",
    PSYD = "PsyD",
    LMSW = "LMSW",
    LMHC = "LMHC",
    LCSW = "LCSW",
    MHCLP = "MHC-LP",
    INTERN = "Intern",
    MD = "MD",
    LMSWA = "LMSW-A",
    LMHCA = "LMHC-A",
    LMHCB = "LMHC-B",
    LMHCC = "LMHC-C",
    PSYDA = "PsyD-A",
    PSYDB = "PsyD-B",
    PSYDC = "PsyD-C",
    PHDA = "PhD-A",
    PHDB = "PhD-B",
    PHDC = "PhD-C",
    LCSWA = "LCSW-A",
    LCSWB = "LCSW-B",
    LCSWC = "LCSW-C",
    EXTERN = "Extern",
}

export enum PaymentCardStatus {
    VALIDATED = "validated",
    UNVALIDATED = "unvalidated",
}

export enum RelationshipToPrimaryInsured {
    SELF = "self",
    SPOUSE = "spouse",
    CHILD = "child",
    OTHER = "other",
}

export enum UserSex {
    MALE = "male",
    FEMALE = "female",
    UNKNOWN = "unknown",
}

export enum InsuranceType {
    MEDICARE = "medicare",
    MEDICAID = "medicaid",
    TRICARE = "tricare",
    CHAMPVA = "champva",
    GROUP_HEALTH_PLAN = "group_health_plan",
    FECA_BLK_LUNG = "feca_blk_lung",
    Other = "other",
}

export enum ClientDocumentTypes {
    CONSENT_FORM = "consent form",
    INSURANCE_SCAN = "insurance scan",
    ID_CARD = "id_card",
    APPEAL = "appeal",
    AUTHORIZATION = "authorization",
    CORRESPONDENCE_WITH_PATIENTS = "correspondence_with_patients",
    EXPLANATION_OF_BENEFITS = "explanation_of_benefits",
    OTHER = "other",
}

export enum InsuranceTabSubModals {
    INFO = "info",
    DOCUMENT = "document",
    EDIT = "edit",
    UPLOAD_DOCUMENT = "upload-document",
}

export interface IInsuranceProvidersFilterTypes {
    insurance_provider_name?: string;
    client_id?: string;
}

export interface IClientCreditCardDetails {
    payment_card_id: string;
    merchant_customer_id: string;
    profile_id: string;
    payment_profile_id: string;
    first_name: string;
    last_name: string;
    masked_card_number: string;
    expiration_date: string;
    billing_address: string;
    zip_code: string;
    card_type: ClientCardTypes;
    state: string;
    country: string;
    card_status: PaymentCardStatus;
    created_source: string;
    is_current: boolean;
    card_label: string;
}

export interface INewCreditCardDetails {
    card_number: string;
    card_cvv: string;
    expiration_month: number;
    expiration_year: number;
    first_name: string;
    last_name: string;
    billing_address: string;
    state: string;
    zip_code: string;
    country: string;
    card_label: string;
}

export interface ICardHistory {
    trans_id: string;
    invoice_number: string;
    transaction_status: string;
    submit_time_utc: string;
    first_name: string;
    last_name: string;
    settlement_time_utc: string;
    settle_amount: number;
    payment_description: string;
}

export interface IClientInsuranceProvider {
    insurance_provider_id: string;
    name: string;
    alias: string;
}

export interface ICardFilterTypes {
    page?: number;
    limit?: number;
}

export interface IClientInsuranceAttachment {
    id: string;
    name: string;
    url: string;
    metadata: {
        content_type: string;
    } | null;
    third_party_type: "dropbox" | "google_drive" | null;
}

export interface IClientInsuranceDetails {
    insurance_id: string;
    name: string;
    is_current: boolean;
    employer_name: string;
    non_client_primary_insured_name: string;
    start_date: string;
    end_date: string;
    insurance_member_id: string;
    is_cal_year: boolean;
    last_verified: string | null;
    non_client_primary_insured_dob: string;
    primary_insured_address: string;
    primary_insured_name: string;
    primary_insured_dob: string;
    financial_assistance_beneficiary: string;
    attached_files: IClientInsuranceAttachment[] | null;
    primary_insured_state: string;
    primary_insured_zipcode: string;
    status: ProviderStatus;
    insurance_accepted: boolean;
    relationship_to_primary_insured?: RelationshipToPrimaryInsured;
    primary_insured_city?: string;
    primary_insured_phone_no?: string;
    primary_insured_sex?: UserSex;
    insured_policy_group?: string;
    insurance_type?: InsuranceType;
    accumulations_start_date?: string;
    accumulations_end_date?: string;
    effective_date?: string;
    policy_effective_start_date?: string;
    policy_effective_end_date?: string;
}

export interface IClientNewInsuranceDetails {
    author?: string;
    insurance_provider_id: string;
    is_cal_year?: boolean;
    start_date?: string;
    end_date?: string;
    employer_name: string;
    insurance_member_id?: string;
    last_verified?: string;
    non_client_primary_insured_name?: string;
    non_client_primary_insured_dob?: string;
    primary_insured_address?: string;
    primary_insured_state?: string;
    primary_insured_zipcode?: string;
    relationship_to_primary_insured?: RelationshipToPrimaryInsured;
    primary_insured_city?: string;
    primary_insured_phone_no?: string;
    primary_insured_sex?: UserSex;
    primary_insured_dob?: string;
    insured_policy_group?: string;
    insurance_type?: InsuranceType;
}

export interface IClientEditInsuranceDetails {
    insurance_provider_id: string;
    is_cal_year?: boolean;
    start_date?: string;
    end_date?: string;
    employer_name: string;
    insurance_member_id?: string;
    last_verified?: string | null;
    non_client_primary_insured_name?: string | null;
    non_client_primary_insured_dob?: string | null;
    primary_insured_address?: string;
    primary_insured_state?: string;
    primary_insured_zipcode?: string;
    status: ProviderStatus;
    insurance_accepted: boolean;
    relationship_to_primary_insured?: RelationshipToPrimaryInsured;
    primary_insured_city?: string;
    primary_insured_phone_no?: string;
    primary_insured_sex?: UserSex;
    primary_insured_dob?: string;
    insured_policy_group?: string;
    insurance_type?: InsuranceType;
    accumulations_start_date?: string;
    accumulations_end_date?: string;
    effective_date?: string;
    policy_effective_start_date?: string;
    policy_effective_end_date?: string;
}
export interface IClientDocumentFilters {
    client_id: string;
    page?: number;
    limit?: number;
    type?: ClientDocumentTypes;
}

export interface IClientDocument {
    document_id: string;
    title: string;
    type: string;
    url: string;
    uploaded_by: {
        user_id: string;
        first_name: string;
        last_name: string;
    };
    uploaded_date: string;
    metadata: {
        content_type: string;
    } | null;
    third_party_type: "dropbox" | "google_drive" | null;
}

export enum ClientDocumentType {
    CONSENT_FORM = "consent form",
    INSURANCE_SCAN = "insurance scan",
    ID_CARD = "id_card",
    APPEAL = "appeal",
    AUTHORIZATION = "authorization",
    CORRESPONDENCE_WITH_PATIENTS = "correspondence_with_patients",
    EXPLANATION_OF_BENEFITS = "explanation_of_benefits",
    OTHER = "other",
}
export interface IClientDocumentsListFilters {
    client_id: string;
    page?: number;
    limit?: number;
    type?: ClientDocumentType;
    document_id?: string;
}

export interface IClientProviderHistory {
    provider_id: string;
    provider_name: string;
    is_current: boolean;
    credential: ProviderCredential;
    service: SessionServiceType;
    no_of_sessions: number;
    first_session_date: string;
    last_seen: string;
    status: ProviderStatus;
    delivery_option?: string;
    session_id: string;
    appointment: {
        session_day: string;
        session_start_hr: number;
        session_start_min: number;
        session_end_hr: number;
        session_end_min: number;
        recurrence_rule: RecurrenceRule;
    };
}

export interface IClientProviderHistoryListFilters {
    limit: number;
    page: number;
}

export enum ClientMemoCategories {
    INTAKE = "intake",
    BILLING = "billing",
    CLIENT_SERVICE = "client services",
    REPORTS = "reports",
    NOTES = "notes",
    PHONE_NOTES = "phone notes",
}

export enum ClientMemoStatus {
    ACTIVE = "active",
    PAUSED = "paused",
}

export enum ClientMemoType {
    USER = "user",
    SYSTEM = "system",
}

export interface IClientMemo extends ISortTable {
    memo_id: string;
    category: ClientMemoCategories;
    date_created: string;
    type: "All" | "system" | "user";
    author: {
        first_name: string;
        last_name: string;
        user_id: string;
    } | null;
    description: string;
    date_updated: string;
    updated_by: {
        first_name: string;
        last_name: string;
    };
    memo_status: ClientMemoStatus;
}

export interface INewMemo {
    author?: string;
    category: ClientMemoCategories;
    description: string;
}

export interface IClientMemoListFilterTypes {
    author_name?: string;
    type?: string;
    category?: string[];
    from_date?: null | string;
    to_date?: null | string;
    page?: number;
    limit?: number;
    memo_status?: string;
}

export interface IClientNotesListFilterTypes {
    provider_name?: string;
    search_string?: string;
    status?: string[];
    type: string[];
    from_date: null | string;
    to_date: null | string;
    page: number;
    limit: number;
    show_archived?: string;
    sort_attribute?: string;
    sort_order?: string;
    show_flagged_notes?: string;
}

export interface IClientNote {
    note_id: string;
    date_of_service: string;
    note_type: NoteTypes;
    provider: {
        user_id: string;
        last_name: string;
        first_name: string;
    };
    note_url: string;
    status: NoteStatus;
    created_at: string;
    flagged_for_audit_status?: FlaggedNoteForAuditStatus;
    flagged_for_audit_notes?: string;
    author?: {
        id: string;
        first_name: string;
        last_name: string;
    };
}

export enum ClientFeeValues {
    MASTERS_FEE = "masters fee",
    DR_FEE = "drs fee",
    INTERN_FEE = "intern fee",
    TESTING_FEE = "testing fee",
    PSYCH_FEE = "psych fee",
    INN_MASTERS_FEE = "inn masters fee",
    DR_FEE_A = "dr_fee_a",
    DR_FEE_B = "dr_fee_b",
    DR_FEE_C = "dr_fee_c",
    LIC_FEE = "lic_fee",
    LIC_A_FEE = "lic_a_fee",
    LIC_B_FEE = "lic_b_fee",
    LIC_C_FEE = "lic_c_fee",
}

export interface IClientFee {
    fee_id: string;
    cal_year: number;
    start_date: string;
    end_date: string;
    self_pay: boolean;
    accepts_assignment: boolean;
    innded: string | null;
    innco: string | null;
    innoop: string | null;
    inncoins: string | null;
    oonded: string | null;
    oonco: string | null;
    oonoop: string | null;
    ooncoins: string | null;
    ucr: string | null;
    service_type?: string | null;
    created_at: string;
    updated_by: {
        author_id?: string;
        first_name: string;
        last_name?: string;
        email?: string;
        preferred_name?: string;
    };
    updated_by_intake_user: string | null;
    alt_email_sent: boolean;
    updated_at: string;
    reason_for_update?: string;
    fee_values: {
        type: ClientFeeValues;
        value: string;
        fee_value_id: string;
    }[];
    providers: {
        provider_id: string;
        credential: ProviderCredential;
        user_id: string;
        service: SessionServiceType;
        first_name: string;
        last_name: string;
        pronouns: string | null;
        preferred_name: string | null;
        start_date: string;
        session_count: number;
        status: ProviderStatus;
    }[];
}

export interface IEditClientFee {
    cal_year: number;
    start_date: string;
    end_date: string;
    self_pay?: boolean;
    accepts_assignment?: boolean;
    alt_email_sent?: boolean;
    reason_for_update: string;
    masters_fee?: string;
    drs_fee?: string;
    intern_fee?: string;
    testing_fee?: string;
    psych_fee?: string;
    lic_fee?: string;
    inn_masters_fee?: string;
}

export interface IAddClientFlag {
    content: string;
}

export interface IClientFlag {
    client_flag_id: string;
    content: string;
}

export interface IAddSessionContent {
    provider_id: string;
    service: string;
    session_start_time: string;
    session_end_time: string;
    recurrence_rule: string;
    delivery_option: string;
    start_date: string;
}

export interface IAddClientSession {
    sessions: IAddSessionContent[];
}

export interface IAddClientSessionResponse {
    created_sessions: string[];
    failed_sessions: {
        data: IAddClientSession;
        error: string;
    }[];
}

export interface IEditSessionContent {
    session_id: string;
    service?: string;
    start_date: string;
    session_start_time: string;
    session_end_time: string;
    recurrence_rule: string;
    delivery_option?: string;
    status?: string;
}

export interface IEditClientSession {
    sessions: IEditSessionContent[];
}

export interface IEditClientSessionResponse {
    edited_sessions: string[];
    failed_sessions: {
        data: {
            session_id: string;
            service: string;
            session_start_time: string;
            session_end_time: string;
            recurrence_rule: string;
            delivery_option?: string;
        };
        error: string;
    }[];
}

export interface IDeleteClientSession {
    session_ids: string[];
}

export interface IDeleteClientSessionResponse {
    deleted_sessions: string[];
    failed_sessions: {
        data: {
            session_id: string;
            error: string;
        };
    }[];
}

export interface IValidateCardPayload {
    clientId: string;
    paymentCardId: string;
}

export interface IDeleteCardsPayload {
    clientId: string;
    card_ids: string[];
}

export interface IDeleteCreditCardsResponse {
    deleted_card_ids: string[];
    undeleted_card_ids: {
        card_id: string;
        reason: string;
    }[];
}

export interface IDeleteInsuranceInformationPayload {
    insurance_ids: string[];
}

export interface IDeleteInsuranceInformationResponse {
    deleted_insurance_ids: string[];
    undeleted_insurance_ids: {
        insurance_id: string;
        reason: string;
    }[];
}
